import { Lock } from "lucide-react";
import * as React from "react";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle, DiscardGuard,
} from "@/components/ui/dialog";
import { Callout } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Textarea } from "@/components/ui/input";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useHoldRecording, useReleaseRecordingHold } from "@/lib/queries";
import {
  MAX_HOLD_REASON, holdDetail, holdReasonError, keepingLabel, keepingState,
} from "@/lib/recording-hold";
import { atLeast, type Recording } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * The last row of a call's recording block: how long the recording is kept, and the legal hold.
 *
 * It answers the question a retention window raises for whoever is looking at one call: is this
 * going to be deleted, and when. Everyone who can open the call reads the answer. An admin can
 * also change it for this one call, by putting it on legal hold or releasing it.
 *
 * A hold is rare and deliberate, so placing one asks for a reason and releasing one asks twice:
 * a released call that is already past its window is deleted within the hour, and that cannot be
 * undone. The reason and who placed the hold show for an admin only, which is the server's rule
 * and not this component's: it draws what it is given.
 */
export function RecordingKeeping({
  callId,
  recording,
}: {
  /** The call the dialog was opened on: the id the recording query is keyed by. */
  callId: string;
  recording: Recording;
}) {
  const { user } = useAuth();
  const canHold = atLeast(user?.role, "admin");
  const [placing, setPlacing] = React.useState(false);
  const [releasing, setReleasing] = React.useState(false);
  const toast = useToast();
  const release = useReleaseRecordingHold();
  const state = keepingState(recording);
  const detail = holdDetail(recording);

  const confirmRelease = () =>
    release.mutate(
      { callId },
      {
        onSuccess: () => {
          toast.success("Legal hold released", "The retention window applies to this call again.");
          setReleasing(false);
        },
        onError: (e) =>
          toast.error(
            "Could not release the hold",
            e instanceof ApiError ? e.message : "Please try again.",
          ),
      },
    );

  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2">
      <span className="min-w-0 text-xs">
        <span
          className={cn(
            "inline-flex items-center gap-1.5",
            state === "on-hold" ? "font-medium text-text" : "text-text-subtle",
          )}
        >
          {state === "on-hold" && <Lock className="size-3 text-warning-11" aria-hidden />}
          {keepingLabel(recording)}
        </span>
        {state === "on-hold" && detail && (
          <span className="mt-0.5 block text-text-muted">{detail}</span>
        )}
      </span>
      {canHold &&
        (state === "on-hold" ? (
          <Button size="sm" onClick={() => setReleasing(true)}>
            Release hold
          </Button>
        ) : (
          <Button size="sm" onClick={() => setPlacing(true)}>
            Put on legal hold
          </Button>
        ))}

      <HoldDialog open={placing} onOpenChange={setPlacing} callId={callId} />
      <ConfirmDialog
        open={releasing}
        onOpenChange={setReleasing}
        title="Release the legal hold?"
        description="The retention window applies to this call again. If the call is already past it, the recording and its transcripts are deleted within the hour. This cannot be undone."
        confirmLabel="Release hold"
        destructive
        loading={release.isPending}
        onConfirm={confirmRelease}
      />
    </div>
  );
}

function HoldDialog({
  open, onOpenChange, callId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  callId: string;
}) {
  const toast = useToast();
  const hold = useHoldRecording();
  const [reason, setReason] = React.useState("");
  const [touched, setTouched] = React.useState(false);
  const [formError, setFormError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setReason("");
    setTouched(false);
    setFormError(null);
  }, [open]);

  const error = touched ? holdReasonError(reason) : undefined;

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setTouched(true);
    setFormError(null);
    if (holdReasonError(reason)) return;
    try {
      await hold.mutateAsync({ callId, reason: reason.trim() });
      toast.success("Call put on legal hold", "Nothing deletes it until the hold is released.");
      onOpenChange(false);
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Could not place the hold.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DiscardGuard changed={reason.trim() !== ""} />
        <DialogHeader>
          <DialogTitle>Put this call on legal hold</DialogTitle>
          <DialogDescription>
            The recording and the call's transcripts are kept until an admin releases the hold.
            The retention window does not delete them, and neither can anyone else.
          </DialogDescription>
        </DialogHeader>
        <DialogForm onSubmit={onSubmit} noValidate>
          <DialogBody className="space-y-4">
            {formError && <Callout tone="danger">{formError}</Callout>}
            <Field
              label="Reason"
              htmlFor="hold-reason"
              required
              hint="Who asked for it and what for. Admins see this; reps and supervisors only see that the call is on hold."
              error={error}
            >
              <Textarea
                className="font-sans text-sm"
                value={reason}
                maxLength={MAX_HOLD_REASON}
                invalid={Boolean(error)}
                onChange={(e) => setReason(e.target.value)}
                onBlur={() => setTouched(true)}
                autoFocus
              />
            </Field>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="default" loading={hold.isPending}>
              Put on hold
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}
